import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';

import { useFeatures } from '@features/FeatureToggle';
import { getStripeSubscriptions } from '@features/Stripe/api';
import {
  LEARN_MORE_JW,
  LEARN_MORE_VARIANT,
} from '@features/Stripe/components/LearnMoreList/additionalInfo';
import type {
  TpLearnMoreData,
  TpStripeSubscription,
  TpStripeSubscriptionResponse,
} from '@features/Stripe/types';
import { isDevelopment, pipe } from 'src/utils';
import { useGetAppName } from 'src/utils/hooks';

export const useGetStripeSubscriptions = ({
  productId,
  wasTried,
}: {
  productId: string | undefined;
  wasTried: boolean;
}) => {
  const {
    introOffer: isIntroOfferEnabled,
    comboPaywall: isComboPaywallEnabled,
    shortLinkPayFeature: isShortLinkPayFeatureEnabled,
    superYouthUpgradePaywall: isSuperYouthUpgradePaywallEnabled,
    japaneseWalkingFeature: isJwFeature,
    japaneseWalking2Feature: isJw2Feature,
    usFeature,
  } = useFeatures();

  const app = useGetAppName();

  const linksMap: Record<string, string> = usFeature
    ? isDevelopment
      ? {
          '1-MONTH PLAN': 'https://buy.stripe.com/test_3cI00jakY4RJfWmh1i8og00',
          '3-MONTH PLAN': 'https://buy.stripe.com/test_eVq3cvakYesj25wdP68og01',
          '6-MONTH PLAN': 'https://buy.stripe.com/test_dRm14n64I0Bt8tU12k8og02',
        }
      : {
          '1-MONTH PLAN': 'https://buy.stripe.com/3cI00jakY4RJfWmh1i8og00',
          '3-MONTH PLAN': 'https://buy.stripe.com/eVq3cvakYesj25wdP68og01',
          '6-MONTH PLAN': 'https://buy.stripe.com/dRm14n64I0Bt8tU12k8og02',
        }
    : isDevelopment
      ? {
          '1-MONTH PLAN': 'https://buy.stripe.com/test_3cI5kDfwx60e1cAcIj5J603',
          '3-MONTH PLAN': 'https://buy.stripe.com/test_7sY14nckl3S608wcIj5J604',
          '6-MONTH PLAN': 'https://buy.stripe.com/test_aFa14nfwx1JY6wUdMn5J605',
        }
      : {
          '1-MONTH PLAN': 'https://buy.stripe.com/5kQ00j0BD2O28F223F5J600',
          '3-MONTH PLAN': 'https://buy.stripe.com/fZu28r2JL88m7AYdMn5J601',
          '6-MONTH PLAN': 'https://buy.stripe.com/aFadR9gAB0FU2gEeQr5J602',
        };

  return useQuery({
    queryKey: ['stripe-subscriptions', productId],
    queryFn: useCallback(
      () => getStripeSubscriptions({ productId, app }),
      [productId, app],
    ),
    enabled: !!productId,
    // абсолютно все преобразования данных должны происходить здесь
    select: useCallback(
      (data: TpStripeSubscriptionResponse) => {
        const sortByOrder = (plans: TpStripeSubscription[]) =>
          [...plans].sort((a, b) => a.order - b.order);

        const filterByWasTried =
          (wasTried: boolean) => (plans: TpStripeSubscription[]) =>
            plans.filter((item) =>
              wasTried
                ? item.specialOffer
                : !item.specialOffer && !item.noMoneyDiscount,
            );

        const addUsersCompareBadge = (plans: TpStripeSubscription[]) =>
          plans.map((item) => {
            if (item.name === '3-MONTH PLAN' && isComboPaywallEnabled) {
              return { ...item, usersCompareBadge: true };
            }
            return item;
          });

        const applyIntroOfferTransform = (isIntroOfferEnabled: boolean) => {
          if (!isIntroOfferEnabled)
            return (plans: TpStripeSubscription[]) => plans;

          return (plans: TpStripeSubscription[]) =>
            plans.map((item) => {
              if (
                item.introOfferData &&
                Object.keys(item.introOfferData).length > 0
              ) {
                return item;
              }

              return {
                ...item,
                introOfferData: {
                  fractionalPrice: item.fractionalPrice,
                  fullPriceDiscount: item.fullPriceDiscount,
                  fullPrice: item.fullPrice,
                },
              };
            });
        };

        const getLearnMoreData = (): TpLearnMoreData => {
          if (isJwFeature || isJw2Feature) {
            return LEARN_MORE_JW[0];
          }

          if (
            isComboPaywallEnabled &&
            !isSuperYouthUpgradePaywallEnabled &&
            !isJwFeature
          ) {
            return {
              learnMoreList: LEARN_MORE_VARIANT[0].learnMoreList.slice(0, 3),
            };
          }

          return LEARN_MORE_VARIANT[0];
        };

        const applyLearnMoreData = (
          plans: TpStripeSubscription[],
        ): TpStripeSubscription[] => {
          const learnMoreData = getLearnMoreData();

          return plans.map((item) => ({ ...item, ...learnMoreData }));
        };

        const applyShortLinkPayLinks = (plans: TpStripeSubscription[]) => {
          if (!isShortLinkPayFeatureEnabled) return plans;

          return plans.map((item) => {
            const link = linksMap[item.name];
            return link ? { ...item, checkoutLink: link } : item;
          });
        };

        const processPlans = (
          plans: TpStripeSubscription[],
          wasTried: boolean,
          isIntroOfferEnabled: boolean,
        ): TpStripeSubscription[] =>
          pipe(
            sortByOrder,
            filterByWasTried(wasTried),
            addUsersCompareBadge,
            applyIntroOfferTransform(isIntroOfferEnabled),
            applyLearnMoreData,
            applyShortLinkPayLinks,
          )(plans);

        const processedPlans = processPlans(
          data.plans,
          wasTried,
          isIntroOfferEnabled,
        );

        const no_money_plan = data.plans.find((item) => item.noMoneyDiscount);

        const transformedNoMoneyPlan = no_money_plan
          ? [no_money_plan].map((plan) => {
              const withIntroOffer = applyIntroOfferTransform(
                isIntroOfferEnabled,
              )([plan])[0];
              const withLearnMore = applyLearnMoreData([withIntroOffer])[0];
              // @ts-ignore
              return applyShortLinkPayLinks([withLearnMore])[0];
            })[0]
          : undefined;

        return {
          ...data,
          plans: processedPlans,
          no_money_plan: transformedNoMoneyPlan,
        };
      },
      [
        linksMap,
        wasTried,
        isJwFeature,
        isJw2Feature,
        isIntroOfferEnabled,
        isComboPaywallEnabled,
        isShortLinkPayFeatureEnabled,
        isSuperYouthUpgradePaywallEnabled,
      ],
    ),
  });
};
